import { History } from 'lucide-react';
import { formatDate } from '../lib/navigation';
import type { ProfileVersion } from '../lib/profileVersions';
import { SearchablePicker } from './SearchablePicker';

interface ProfileVersionPanelProps {
  versions: ProfileVersion[];
  selected: ProfileVersion;
  onChange: (version: string) => void;
}

export function ProfileVersionPanel({ versions, selected, onChange }: ProfileVersionPanelProps) {
  const latest = versions[0];
  const isLatest = selected.version === latest.version;
  return (
    <section className="profile-version-panel" aria-label="Profile version and update information">
      <div className="profile-version-main">
        <div className="profile-version-picker">
          <span className="search-field-label">Profile version</span>
          <SearchablePicker
            value={selected.version}
            onChange={onChange}
            label="Profile version"
            dialogLabel="Choose profile version"
            searchLabel="Find a version"
            options={versions.map((release, index) => ({
              value: release.version,
              label: `v${release.version}${index === 0 ? ' · Latest' : ''}`,
              searchText: `${release.version} ${formatDate(release.releasedAt)}`,
              icon: <History size={20} />,
              content: <span className="version-option"><strong>v{release.version}{index === 0 && <> <span>Latest</span></>}</strong>{' '}<time dateTime={release.releasedAt}>{formatDate(release.releasedAt)}</time></span>,
            }))}
          />
        </div>
        <div className="profile-version-notes" aria-live="polite">
          <div><h2>{isLatest ? 'Latest update' : `Version ${selected.version}`}</h2><time dateTime={selected.releasedAt}>{formatDate(selected.releasedAt)}</time></div>
          {selected.changes.map((change) => <p key={change}>{change}</p>)}
        </div>
      </div>
      {!isLatest && (
        <div className="profile-older-version">
          <p><History size={16} aria-hidden="true" /><span>Viewing an older version. <strong>v{latest.version}</strong> is the latest, updated <time dateTime={latest.releasedAt}>{formatDate(latest.releasedAt)}</time>.</span></p>
          <button className="button ghost small" type="button" onClick={() => onChange(latest.version)}>View latest</button>
        </div>
      )}
    </section>
  );
}
